import { DataCollectionService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { QuestionTemplate } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Container, Modal, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { deepCopy } from '../../../common/util';
import { camelCaseToTitleCase } from '../../portfolio-data-asset/utils';
import { emptyQuestionTemplate } from './constants';
import DataCollectionEditQuestion from './DataCollectionEditQuestion';

interface Props {
    activeTab: string;
    visible: boolean;
    setVisible: (visible: boolean) => void;
}

const dataCollectionService = new DataCollectionService();
const schemaService = new SchemaService();
function DataCollectionEditQuestionnaireTemplateModal({ activeTab, visible, setVisible, flash }: Props & FlashContextInterface): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId ?? '';

    const [ loading, setLoading ] = useState(false);
    const [ templateToEdit, setTemplateToEdit ] = useState<QuestionTemplate[]>();
    const [ propertyOptions, setPropertyOptions ] = useState<OptionDefinition[]>([]);

    const dismissModal = (): void => setVisible(false);
    const isMounted = useIsMounted();

    const loadTemplate = async () => {
        setLoading(true);
        try {
            const template = await dataCollectionService.getTemplate(portfolioId, activeTab);
            if (!isMounted()) {
                return;
            }
            setTemplateToEdit(!template.length ? undefined : template);
           
            const properties = await schemaService.listAttributes(`${activeTab}s`, portfolioId);
            if (!isMounted()) {
                return;
            }
            const propertyOptions = properties.map(property => {
                return {
                    value: property.value,
                    label: property.text ? property.text : camelCaseToTitleCase(property.value)
                };
            });
            setPropertyOptions(propertyOptions);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_RETRIEVING_TEMPLATE);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ activeTab ]);

    const deleteQuestion = (question: QuestionTemplate) => {
        const indexToRemove = templateToEdit?.findIndex(template => template === question);
        if (indexToRemove !== undefined && indexToRemove > -1) {
            const updatedTemplate = deepCopy(templateToEdit);
            updatedTemplate.splice(indexToRemove, 1);
            setTemplateToEdit(updatedTemplate);
        }
    };

    const updateQuestion = (currentQuestion: QuestionTemplate, updatedQuestion: QuestionTemplate) => {
        const questionToUpdateIndex = templateToEdit?.findIndex(template => template === currentQuestion);
        if (questionToUpdateIndex !== undefined && questionToUpdateIndex > -1) {
            const updatedTemplate = deepCopy(templateToEdit);
            updatedTemplate[questionToUpdateIndex] = updatedQuestion;
            setTemplateToEdit(updatedTemplate);
        }
    };

    const addQuestion = () => {
        const updatedTemplate: QuestionTemplate[] = deepCopy(templateToEdit ?? []);
        updatedTemplate.push(emptyQuestionTemplate);
        setTemplateToEdit(updatedTemplate);
    };

    const saveTemplate = async () => {
        if (!templateToEdit) {
            return;
        }
        setLoading(true);
        try {
            await dataCollectionService.createTemplate(portfolioId, activeTab, templateToEdit);
            setVisible(false);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_SAVING_TEMPLATE);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    const isSaveBtnDisabled = (): boolean => {
        if (!templateToEdit) {
            return true;
        }

        for (let index=0; index < templateToEdit.length; index++) {
            if (templateToEdit[index].label.length === 0 
                || templateToEdit[index].questionText.length === 0 
                || templateToEdit[index].resourceReference.length === 0) 
            {
                return true;
            }
        };
        return false;
    };

    const getQuestions = (): JSX.Element => {
        const questions = templateToEdit?.map((question, index) => {
            return (
                <Container key={index}>
                    <Box float='right'>
                        <Button iconName='close' variant='icon' onClick={() => deleteQuestion(question)}/>
                    </Box>
                    <DataCollectionEditQuestion
                        key={index}
                        question={question}
                        updateQuestion={updateQuestion}
                        activeTab={activeTab}
                        propertyOptions={propertyOptions}
                    />
                </Container>
            );
        });
        return (
            <SpaceBetween size='m'>
                {questions ?? []}
            </SpaceBetween>
        );
    };

    const footer = (
        <Box float='right'>
            <SpaceBetween size='m' direction='horizontal'>
                <Button onClick={addQuestion}>{constants.ADD_QUESTION}</Button>
                <Button onClick={saveTemplate} disabled={isSaveBtnDisabled()}>{constants.SAVE_BUTTON_TEXT}</Button>
                <Button onClick={dismissModal}>{constants.CLOSE_BUTTON_TEXT}</Button>
            </SpaceBetween>
        </Box>
    );

    const content = loading ? <Spinner size='large'/> : getQuestions();

    return (
        <Modal
            visible={visible}
            onDismiss={dismissModal}
            header={`${constants.EDIT_BUTTON_TEXT} ${activeTab} ${constants.QUESTIONNAIRE_TEMPLATE}`}
            footer={footer}
            size='large'
        >
            {content}
        </Modal>
    );
}

export default withFlashContext(DataCollectionEditQuestionnaireTemplateModal);
